import * as Engine from "./engine";
import * as Template from "./template";
import * as Paragraph from "./paragraph";
import { resolveSectionValues } from "./types";
import * as Blog from "./blog";

const MISSING = `<div style="color:#b00;font:12px monospace">Section not found</div>`;

// A section that could not be FETCHED, which is a different thing from one that
// is not there.
//
// Every caller resolving a page's sections used to collapse the two: a rejected
// query became `[sectionId, null]`, the id went missing from the map, and the
// composer printed "Section not found" — for a dropped connection, an expired
// token, an access that was revoked, a throttled table. The person reading it
// goes looking for a section they never deleted, and on a page they own the
// message is simply false.
//
// The reason is carried and escaped: it comes from an error object and reaches
// the document as text, the same rule the four error branches in ./template
// follow. Truncated because a stack in a preview pane is not a message.
const REASON_LIMIT = 120;

const unavailable = (reason) => {
  const text = reason ? Engine.escapeHtml(String(reason).slice(0, REASON_LIMIT)) : "";

  return `<div style="color:#b00;font:12px monospace">Section could not be loaded${
    text ? `: ${text}` : ""
  }</div>`;
};

// The key a resolver marks an unfetchable section with, and the constructor for
// it. An object rather than a sentinel value because the map it goes into is
// `id -> section` and every reader of that map already tests for presence; this
// is present, and says why it is useless.
//
// `__` because a section is a stored document with author-chosen fields, and
// this must not be one a person could write. The schema has no property
// starting with an underscore.
export const UNAVAILABLE = "__unavailable";

export const sectionUnavailable = (reason) => ({
  [UNAVAILABLE]: reason === undefined || reason === null ? "" : String(reason),
});

// Render one element to its three streams.
//
// `section` and `code` differ only in where the content comes from: a `section`
// looks its content up by id, a `code` carries it inline. Everything after that
// — resolving the values, compiling the template, wrapping it in the frame — is
// the same path, which is the point of having both.
//
// `paragraph` is the odd one and deliberately so: it produces a body and
// nothing else, and it never reaches Template.render. There is no template to
// compile, so there is no EJS pass over it — which is not a limitation being
// tolerated but the reason the type is safe to offer. Its content is written in
// a rich-text editor by someone who is typing prose, and a `<%` they happened
// to type is prose too.
//
// Bypassing this function is NOT enough to make that true of `{{...}}`, and the
// difference is worth knowing before changing either side: ./page's `bodyOf`
// interpolates the ASSEMBLED body, so every element's output meets that pass
// whatever route it took to get here. What keeps a paragraph out of it is the
// brace escape in ./paragraph, not this branch.
// A blog element with nothing loaded behind it. Three cases and they read
// differently on the page: the thing is not there (an unpublished slug, a
// removed collection), the read for it failed, or nothing was loaded at all —
// which is what the editor's frame shows in the instant before its data
// arrives, and what a caller that never asked for data gets.
const NOUNS = {
  [Blog.POST]: "Post",
  [Blog.LIST]: "Posts",
  [Blog.COLLECTION]: "Collection",
};

const blogMissing = (type) =>
  `<div style="color:#b00;font:12px monospace">${NOUNS[type]} not found</div>`;

const blogUnavailable = (type, reason) => {
  const text = reason ? Engine.escapeHtml(String(reason).slice(0, REASON_LIMIT)) : "";
  return `<div style="color:#b00;font:12px monospace">${NOUNS[type]} could not be loaded${
    text ? `: ${text}` : ""
  }</div>`;
};

const loaded = (entry, type) =>
  Boolean(entry) &&
  (type === Blog.POST ? Boolean(entry.post)
    : type === Blog.LIST ? Boolean(entry.page)
      : Boolean(entry.collection));

// The blog branches, kept together because they share their preamble: the
// entry for this element out of `data`, the site's template for its kind (out
// of the same `data`, resolved by whoever loaded it — see Blog.templateFrom),
// and the site's authors. Nothing here reads `sections`: a blog element
// renders through the site's template, never through a section.
const renderBlog = (element, context, data) => {
  const { type } = element;
  const config = element.config || {};
  const entries = (data && data.elements) || {};
  const entry = entries[element.elementid];

  if (entry && entry[UNAVAILABLE] !== undefined) {
    return { body: blogUnavailable(type, entry[UNAVAILABLE]), style: "", script: "" };
  }

  if (!loaded(entry, type)) {
    return { body: blogMissing(type), style: "", script: "" };
  }

  const template = Blog.templateFrom(data, type);
  const authors = (data && data.authors) || [];

  if (type === Blog.POST) {
    return Blog.renderPost(element, template, entry, context, authors);
  }

  if (type === Blog.LIST) {
    return Blog.renderList(element, template, entry, context, authors);
  }

  return Blog.renderCollection(element, template, entry, context, authors);
};

const renderElement = (element, index, sections, context, data) => {
  const { type, frame } = element;
  // `|| {}` rather than a destructuring default, which only covers `undefined`.
  // `config` is a oneOf resolved by `type`, and extract() returns NULL for a
  // branch that has not been selected — so an element saved with no type yet
  // arrives here as `config: null`, and a destructuring default would let the
  // next read of it throw and take the whole preview down.
  const config = element.config || {};

  let rendered;

  if (type === "section") {
    const section = sections[config.sectionId];

    // Nothing enforces the reference — see model/section's remove — so a
    // dangling one is expected, not exceptional. It renders in place, where the
    // person who removed the section can see which page it broke.
    if (!section) {
      return { body: MISSING, style: "", script: "" };
    }

    // The reference is fine and the fetch was not. Said separately, because
    // "not found" sends somebody looking for a section that is still there.
    if (section[UNAVAILABLE] !== undefined) {
      return { body: unavailable(section[UNAVAILABLE]), style: "", script: "" };
    }

    rendered = Template.render(
      section,
      { types: section.types, values: resolveSectionValues(section) },
      context,
      // A section is shared, so its `data-edit` paths point at the SECTION's
      // values and not the page's. Inline editing is offered for `code`
      // elements only — see editorMarkup in ./template — because typing into a
      // shared section from a page that merely carries it would edit every
      // other page that carries it too, with nothing on screen saying so.
      null
    );
  } else if (type === "code") {
    rendered = Template.render(
      config,
      { types: config.types, values: resolveSectionValues(config) },
      context,
      context.target === "editor" ? { index } : null
    );
  } else if (type === "paragraph") {
    // No style, no script and no `data-edit`: a paragraph has one field and it
    // is edited on the element's own screen, so there is nothing for inline
    // editing to offer that the form beside the preview does not already.
    rendered = { body: Paragraph.render(config.content), style: "", script: "" };
  } else if (Blog.isBlogType(type)) {
    rendered = renderBlog(element, context, data);
  } else {
    return { body: "", style: "", script: "" };
  }

  let body = rendered.body;
  if (frame) {
    try {
      body = Engine.render(frame, { content: rendered.body });
    } catch (e) {
      // Escaped for the reason ./template gives at its own error branches: the
      // message carries author-chosen text back out.
      body = `<div style="color:#b00;font:12px monospace">Frame error: ${Engine.escapeHtml(
        e.toString()
      )}</div>`;
    }
  }

  return { body, style: rendered.style, script: rendered.script };
};

// Walk a page's elements in order and slot each one.
//
// `tags` decide placement: "page:header" and "page:footer" pull an element out
// of the flow; everything else lands in the body in array order. That is the
// only structural mechanism — elements are a flat list, not a tree — and it is
// what lets one shared navbar sit above pages whose bodies have nothing in
// common.
// `data` is what the blog elements need, loaded before this runs — see
// ./blog's requirements() and the two callers that fulfil it. Shaped
// `{ authors: [...], templates: { [kind]: template }, elements: { [elementid]:
// entry } }`, and optional: a page with no blog element never reads it.
export const render = (page, sections, context, editorToken = "", data = {}) => {
  const out = { header: "", body: "", footer: "", style: "", script: "" };

  const elements = page.elements || [];

  for (let index = 0; index < elements.length; index += 1) {
    const element = elements[index];
    const res = renderElement(element, index, sections, context, data);
    const tags = element.tags || [];

    // In the editor every element is preceded by a comment naming its index.
    // That is the entire mechanism behind "highlight the selected element" and
    // "double-click to jump to it": the preview frame walks the comment nodes
    // and treats the element that follows one as that element's root.
    //
    // A comment rather than a wrapper or an attribute, because it adds no node
    // and changes no selector. A wrapper would break the first `body > nav` a
    // tenant writes, and an attribute cannot be placed — the composer sees a
    // string of markup, not a tree, and an element's template can emit several
    // top-level nodes or none.
    //
    // AND A TOKEN THE FRAME MINTED FOR THIS MOUNT. Without it the marker is
    // `<!-- element 3 -->`, which is nine characters a tenant may type into
    // their own markup — by accident, or to point the editor's highlight and
    // its double-click-to-open at a node belonging to a different element.
    // The token is not in the render context and no template can read it, so
    // stored markup cannot carry a live one; a frame that finds a marker it did
    // not mint ignores it. See preview/mount.
    //
    // Absent, the bare form is emitted. Nothing composes for the editor without
    // one today, and a caller that forgets is better off with a preview whose
    // highlighting works than with one whose elements are all unaddressable.
    const marker =
      context.target === "editor"
        ? `\n<!-- element ${index}${editorToken ? ` ${editorToken}` : ""} -->\n`
        : "";

    const body = `${marker}${res.body}`;

    if (tags.includes("page:header")) {
      out.header += body;
    } else if (tags.includes("page:footer")) {
      out.footer += body;
    } else {
      out.body += body;
    }

    out.style += res.style;
    out.script += res.script;
  }

  return out;
};
